import { ChevronDownIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Badge,
    Box,
    Button,
    Center,
    Flex,
    HStack,
    Icon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    ColumnFiltersState,
    createColumnHelper,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { emailRepository } from 'api/EmailRepository';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import React, { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';
import { IoMdAdd, IoMdDownload } from 'react-icons/io';
import { MdEdit } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { IoMdArrowUp } from "react-icons/io";


type EmailTemplate = {
    id: string;
    name: string;
    subject: string;
    body: string;
    isActive: boolean;
    createdAt: string;
    modifiedAt: string;
};

type AlertStatus = "success" | "error" | "info" | "warning" | "loading";

type Props = {
    tableData: EmailTemplate[];
    globalFilter: string;
    setGlobalFilter: Dispatch<SetStateAction<string>>;
};

const statusFilterFn: FilterFn<EmailTemplate> = (row, columnId, value) => {
    if (value === 'All' || value === '') {
        return true;
    }
    const isActive = row.getValue(columnId) as boolean;
    const statusLabel = isActive ? 'Active' : 'Inactive';
    return statusLabel === value;
};


export default function EmailTable({ tableData, globalFilter, setGlobalFilter }: Props) {
    const textColor = useColorModeValue('gray.800', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const subTextColor = useColorModeValue('gray.700', 'gray.300');
    const hoverBg = useColorModeValue('blue.50', 'whiteAlpha.100');
    const cellColor = useColorModeValue('secondaryGray.900', 'white');
    const navigate = useNavigate();
    const [data, setData] = useState<EmailTemplate[]>(tableData);
    const [statusFilter, setStatusFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState<EmailTemplate | null>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [centerAlertVisible, setCenterAlertVisible] = useState(false);
    const [centerAlertStatus, setCenterAlertStatus] = useState<AlertStatus>('info');
    const [centerAlertMessage, setCenterAlertMessage] = useState('');

    const showCenteredAlert = (status: AlertStatus, message: string) => {
        setCenterAlertStatus(status);
        setCenterAlertMessage(message);
        setCenterAlertVisible(true);
        setTimeout(() => setCenterAlertVisible(false), 2000);
    };

    const onCloseDelete = () => {
        setIsDeleteOpen(false);
        setSelectedEmail(null);
    };

    const confirmDelete = async () => {
        if (!selectedEmail) return;
        try {
            await emailRepository.deleteEmail(selectedEmail.id);
            setData((prev: EmailTemplate[]) => prev.filter((n: EmailTemplate) => n.id !== selectedEmail!.id));
            showCenteredAlert('success', 'Template deleted successfully.');
        } catch (err) {
            showCenteredAlert('error', 'Failed to delete template.');
        } finally {
            onCloseDelete();
        }
    };

    const handleStatusChange = (status: string) => {
        setStatusFilter(status);
        const newFilters = columnFilters.filter(f => f.id !== 'isActive');

        if (status !== '' && status !== 'All') {
            newFilters.push({ id: 'isActive', value: status });
        }

        setColumnFilters(newFilters);
    };

    const handleExport = async () => {
        try {
            const blob = await emailRepository.exportEmails();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Templates.csv");
            document.body.appendChild(link);
            link.click();
            link.remove();

            showCenteredAlert("success", "Templates exported successfully!");
        } catch (err) {
            showCenteredAlert("error", "Failed to export templates.");
        }
    };

    const headerTextStyle = {
        fontSize: '12px',
        fontWeight: '700',
        letterSpacing: '0.6px',
        textTransform: 'uppercase' as const,
        color: 'gray.400',
    };

    const cellTextStyle = {
        fontSize: '15px',
        fontWeight: '500',
        color: cellColor,
    };

    const columnHelper = createColumnHelper<EmailTemplate>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: () => <Text sx={headerTextStyle}>Name</Text>,
                cell: (info) => <Text sx={cellTextStyle}>{info.getValue()}</Text>,
            }),
            columnHelper.accessor('subject', {
                header: () => <Text sx={headerTextStyle}>Subject</Text>,
                cell: (info) => <Text color={cellColor} fontWeight='400' fontSize='15px'>{info.getValue()}</Text>,
            }),
            columnHelper.accessor('isActive', {
                header: () => <Text sx={headerTextStyle}>Status</Text>,
                filterFn: statusFilterFn,
                cell: (info) => {
                    const active = info.getValue();
                    return (
                        <Badge fontSize='14px' colorScheme={active ? 'green' : 'red'}>
                            {active ? 'Active' : 'Inactive'}
                        </Badge>
                    );
                },
            }),
            columnHelper.accessor('createdAt', {
                header: () => <Text sx={headerTextStyle}>Created At</Text>,
                cell: (info) => <Text fontSize='15px' fontWeight='500'>{new Date(info.getValue() as string).toLocaleString()}</Text>,
            }),

            columnHelper.display({
                id: 'actions',
                header: () => <Text sx={headerTextStyle} textAlign="center">Actions</Text>,
                cell: (info) => {
                    const row = info.row.original;
                    return (
                        <HStack spacing={2} justify="center">
                            <Tooltip label="Edit Template" hasArrow>
                                <span>
                                    <Icon
                                        as={MdEdit}
                                        w={5}
                                        h={5}
                                        color="blue.500"
                                        cursor="pointer"
                                        _hover={{ color: 'blue.600' }}
                                        onClick={() =>
                                            navigate('/admin/Template/EditTemplate', { state: { email: row } })
                                        }
                                    />
                                </span>
                            </Tooltip>
                        </HStack>
                    );
                },
            }),
        ],
        [navigate, textColor, subTextColor]
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            globalFilter,
            columnFilters,
        },
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <Flex direction="column" w="100%" minH="calc(100vh - 75px)" overflowX="auto">
            <Box overflowY="auto" flex="1" w="100%" p={4}>
                <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={3}>
                        <DebouncedInput
                            value={globalFilter ?? ''}
                            onChange={(value: string) => setGlobalFilter(String(value))}
                            placeholder="Search..."
                        />
                        <Menu>
                            <MenuButton
                                as={Button}
                                rightIcon={<ChevronDownIcon />}
                                bg={useColorModeValue('gray.100', 'gray.700')}
                                borderRadius="full"
                                _hover={{ bg: useColorModeValue('gray.200', 'gray.600') }}
                            >
                                {statusFilter || 'Status'}
                            </MenuButton>
                            <MenuList>
                                <MenuItem onClick={() => handleStatusChange('')}>All</MenuItem>
                                <MenuItem onClick={() => handleStatusChange('Active')}>Active</MenuItem>
                                <MenuItem onClick={() => handleStatusChange('Inactive')}>Inactive</MenuItem>
                            </MenuList>
                        </Menu>
                    </HStack>
                    <HStack spacing={4}>
                        <Button
                            colorScheme="blue"
                            borderRadius="16px"
                            onClick={() => navigate('/admin/Template/AddTemplate')}
                        >
                            <Icon as={IoMdAdd} w="20px" h="20px" color="inherit" /> Add Template
                        </Button>
                        <Button
                            colorScheme="blue"
                            borderRadius="16px"
                            onClick={handleExport}
                            ml={4}
                            mr={2}
                        >
                            <Icon as={IoMdArrowUp} w="20px" h="20px" color="inherit" /> Export
                        </Button>
                    </HStack>
                </Flex>
                <Box overflowX="auto">
                    <Table variant="simple" color="gray.500">
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            cursor="pointer"
                                            textTransform="uppercase"
                                            borderColor={borderColor}
                                            align="center"
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </Th>
                                    ))}
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <Tr
                                        key={row.id}
                                        sx={{
                                            '& td': {
                                                borderBottom: '1px solid',
                                                borderColor: borderColor,
                                                cursor: 'pointer',
                                            },
                                            '&:hover td': { backgroundColor: hoverBg },
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <Td key={cell.id} borderColor={borderColor} minW="150px">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={columns.length} textAlign="center">
                                        No  templates found.
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </Box>
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onCloseDelete} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="md">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Template</AlertDialogHeader>
                        <AlertDialogBody>Are you sure you want to delete this Template?</AlertDialogBody>
                        <Flex justify="flex-end" p={4}>
                            <Button ref={cancelRef} onClick={onCloseDelete} mr={3}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={confirmDelete}>
                                Delete
                            </Button>
                        </Flex>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {centerAlertVisible && (
                <Center
                    position="fixed"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    zIndex={60}
                    pointerEvents="none"
                >
                    <Alert
                        status={centerAlertStatus}
                        variant="solid"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        width="600px"
                        borderRadius="14px"
                        boxShadow="xl"
                        py={10}
                        px={16}
                        pointerEvents="auto"
                    >
                        <AlertIcon boxSize="40px" mr={0} />

                        <Text fontSize="lg" fontWeight="700" mt={1}>
                            {centerAlertMessage}
                        </Text>
                    </Alert>
                </Center>
            )}
        </Flex>
    );
}

function DebouncedInput<T extends string | number>({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: T;
    onChange: (value: T) => void;
    debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = useState(initialValue);
    React.useEffect(() => setValue(initialValue), [initialValue]);
    React.useEffect(() => {
        const timeout = setTimeout(() => onChange(value as T), debounce);
        return () => clearTimeout(timeout);
    }, [value, debounce, onChange]);
    return (
        <SearchBar
            {...props}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value as T)}
            h="44px"
            w={{ lg: '390px' }}
            borderRadius="16px"
        />
    );
}